import React, { ComponentProps, FC } from 'react';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import type {
    //DOMConversionMap,
    //DOMConversionOutput,
    //DOMExportOutput,
    EditorConfig,
    ElementFormatType,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    Spread,
} from 'lexical';
import {
    DecoratorBlockNode,
    SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';

// mui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export const InsertReplyComponent: FC<
    {
        message: string;
        userId: number;
        userName: string;
    } &
    Omit<
        ComponentProps<typeof BlockWithAlignableContents>,
        'children'
    >
> = ({
    message,
    userId,
    userName,
    className,
    format,
    nodeKey
}) => {
    return (
        <BlockWithAlignableContents
            className={className}
            format={format}
            nodeKey={nodeKey}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 400,
                    p: 0.5,
                    my: 1,
                    backgroundColor: (theme) => theme.palette.grey[50],
                    borderColor: (theme) => theme.palette.divider,
                    borderRadius: 1,
                    borderStyle: "solid",
                    borderWidth: 1,
                }}
            >
                <Typography
                    className={"insert-reply"}
                    sx={{
                        display: "block",
                        pt: 0.25,
                        pl: 1,
                        borderLeftColor: (theme) => theme.palette.grey[400],
                        borderLeftStyle: "solid",
                        borderLeftWidth: 2,
                    }}
                >
                    <Typography
                        component="span"
                        paragraph
                        data-user-id={userId}
                        sx={{
                            backgroundColor: "rgba(24, 119, 232, 0.2)",
                        }}
                    >
                        {userName}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            display: "block",
                        }}
                    >
                        {message}
                    </Typography>
                </Typography>
            </Box>
        </BlockWithAlignableContents>
    );
}

export type SerializedInsertReplyNode = Spread<
    {
        message: string;
        userId: number;
        userName: string;
        type: 'insert-reply';
        version: 1;
    },
    SerializedDecoratorBlockNode
>

export class InsertReplyNode extends DecoratorBlockNode {
    __message: string;
    __userId: number;
    __userName: string;

    static getType(): string {
        return 'insert-reply'
    }

    static clone(node: InsertReplyNode): InsertReplyNode {
        return new InsertReplyNode(
            node.__message,
            node.__userId,
            node.__userName,
            node.__format,
            node.__key
        );
    }

    constructor(
        message: string,
        userId: number,
        userName: string,
        format?: ElementFormatType,
        key?: NodeKey
    ) {
        super(format, key);
        this.__message = message;
        this.__userId = userId;
        this.__userName = userName;
    }

    // JSON→Nodeに変換
    static importJSON(serializedNode: SerializedInsertReplyNode): InsertReplyNode {
        const node = $createInsertReplyNode(serializedNode.message, serializedNode.userId, serializedNode.userName);
        node.setFormat(serializedNode.format);
        return node;
    }

    // Node→JSONに変換
    exportJSON(): SerializedInsertReplyNode {
        return {
            ...super.exportJSON(),
            message: this.__message,
            userId: this.__userId,
            userName: this.__userName,
            type: 'insert-reply',
            version: 1,
        }
    }

    updateDOM(): false {
        return false
    }

    decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
        const embedBlockTheme = config.theme.embedBlock || {};
        const className = {
            base: embedBlockTheme.base || '',
            focus: embedBlockTheme.focus || ''
        };
        return (
            <InsertReplyComponent
                className={className}
                format={this.__format}
                message={this.__message}
                nodeKey={this.getKey()}
                userName={this.__userName}
                userId={this.__userId}
            />
        )
    }
}

export const $createInsertReplyNode = (message: string, userId: number, userName: string): InsertReplyNode => {
    return new InsertReplyNode(message, userId, userName);
}

export function $isInsertReplyNode(
    node: LexicalNode | null | undefined,
): node is InsertReplyNode {
  return node instanceof InsertReplyNode;
}
